import { useCallback } from 'react';
import getAccessToken from '../../../api/BearerToken';
import { useRoutes } from '../../../hooks/useRoutes';
import { SmartschoolSchool } from '../../../api/my-account';

type LoginProvider = 'smartschool';
type CompleteIdentityFlow = 'complete' | 'set-email' | 'set-password';

export default function useLoginProvider() {
  const { buildAbsolutePath } = useRoutes();

  const buildQuery = (
    lang: string,
    redirectUri: string,
    accessToken: string,
    provider?: 'zogezegd-leerkracht' | 'p',
  ) => {
    const params = new URLSearchParams({
      language: lang,
      redirect_uri: redirectUri,
      access_token: accessToken,
    });

    if (provider) {
      params.append('provider', provider);
    }

    return params;
  };

  const connect = useCallback(
    async (provider: LoginProvider, lang: string) => {
      const accessToken = await getAccessToken();

      const query = buildQuery(
        lang,
        `${window.location.origin}${buildAbsolutePath(['CONNECT_CALLBACK'])}`,
        accessToken,
      );

      window.location.assign(`${process.env.REACT_APP_P_API_URL}/api/oauth/connect/${provider}?${query}`);
    },
    [buildAbsolutePath],
  );

  const disconnect = useCallback(
    async (id: string, provider: LoginProvider, lang: string) => {
      const accessToken = await getAccessToken();

      const query = buildQuery(
        lang,
        `${window.location.origin}${buildAbsolutePath(['DISCONNECT_CALLBACK'])}`,
        accessToken,
      );

      window.location.assign(`${process.env.REACT_APP_P_API_URL}/api/oauth/disconnect/${provider}/${id}?${query}`);
    },
    [buildAbsolutePath],
  );

  const completeIdentity = useCallback(
    async (flow: CompleteIdentityFlow, lang: string) => {
      const accessToken = await getAccessToken();
      const provider = process.env.REACT_APP_P_STUDIO_GROUP === 'VO' ? 'zogezegd-leerkracht' : 'p';

      const query = buildQuery(
        lang,
        `${window.location.origin}${buildAbsolutePath(['EDIT_CALLBACK'])}`,
        accessToken,
        provider,
      );

      window.location.assign(`${process.env.REACT_APP_P_API_URL}/api/oauth/complete/${flow}?${query}`);
    },
    [buildAbsolutePath],
  );

  const verifySmartschoolAdmin = useCallback(async (language: string, smsSchool: SmartschoolSchool) => {
    const accessToken = await getAccessToken();

    const query = new URLSearchParams({
      language,
      platform: `https://${smsSchool.domain}`,
      access_token: accessToken,
      redirect_uri: `${window.location.href}?openSmsModal=${smsSchool.id}`,
    });

    window.location.assign(`${process.env.REACT_APP_P_API_URL}/api/oauth/validate-admin/smartschool?${query}`);
  }, []);

  return {
    connect,
    disconnect,
    completeIdentity,
    verifySmartschoolAdmin,
  };
}
